import { addDays, formatTime, isSameDay } from '@/helpers/date';
import { SPORT_LIVE_TV_MAX_LIMIT, SPORT_LIVE_TV_SORTS, SPORT_LIVE_TV_STATUSES, SPORT_LIVE_TV_WATCH_ROUTE_NAME } from '@/sport-live-tv/constants';

export const isSportLiveTvEventLive = ({ started_at, is_ended }, now = Date.now()) => !is_ended && now >= started_at * 1000;

export const getSportLiveTvEventStatus = (event, now = Date.now()) => (isSportLiveTvEventLive(event, now) ? SPORT_LIVE_TV_STATUSES.live : SPORT_LIVE_TV_STATUSES.upcoming);

export const getSportLiveTvEventTitle = ({ event_name, team_a, team_b }) => event_name || [team_a, team_b].filter(Boolean).join(' vs ');

export const joinSportLiveTvDetails = (details) => details.filter(Boolean).join(' • ');

export const getSportLiveTvEventSubtitle = ({ category_name, venue_name }) => joinSportLiveTvDetails([category_name, venue_name]);

export const getSportLiveTvStreamPickerSubtitle = (event) => joinSportLiveTvDetails([getSportLiveTvEventTitle(event), event.category_name]);

export const getSportLiveTvEventScore = (event, now = Date.now()) => {
  const { show_score, is_ended, team_a_score, team_b_score } = event;
  if (!show_score || (!is_ended && !isSportLiveTvEventLive(event, now))) return '';

  return `${team_a_score} : ${team_b_score}`;
};

export const formatSportLiveTvStart = (startedAt, labels, locale) => {
  const date = new Date(startedAt * 1000);
  if (Number.isNaN(date.getTime())) return '';

  const time = formatTime(date, locale);
  const now = new Date();

  if (isSameDay(date, now)) return `${labels.today} • ${time}`;
  if (isSameDay(date, addDays(now, 1))) return `${labels.tomorrow} • ${time}`;

  const day = date.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: '2-digit' });

  return `${day} • ${time}`;
};

export const getSportLiveTvEventMeta = (event, scoresVisible, labels, locale) => {
  const score = scoresVisible ? getSportLiveTvEventScore(event) : '';
  const start = getSportLiveTvEventStatus(event) === SPORT_LIVE_TV_STATUSES.upcoming ? formatSportLiveTvStart(event.started_at, labels, locale) : '';

  return joinSportLiveTvDetails([event.category_name, score || start]);
};

export const getSportLiveTvEventLogos = ({ category_logo, team_a_logo, team_b_logo, venue_logo }) => [...new Set([category_logo, team_a_logo, team_b_logo, venue_logo].filter(Boolean))];

export const getSportLiveTvWatchRoute = ({ id }) => ({ name: SPORT_LIVE_TV_WATCH_ROUTE_NAME, params: { id } });

export const filterSportLiveTvEventsByStatus = (events, status) => (status === SPORT_LIVE_TV_STATUSES.all ? events : events.filter((event) => getSportLiveTvEventStatus(event) === status));

export const uniqueSportLiveTvEvents = (events) => [...new Map(events.map((event) => [event.id, event])).values()];

const compareSportLiveTvTitles = (first, second) => getSportLiveTvEventTitle(first).localeCompare(getSportLiveTvEventTitle(second));

const SPORT_LIVE_TV_COMPARATORS = {
  [SPORT_LIVE_TV_SORTS.newest]: (first, second) => second.started_at - first.started_at,
  [SPORT_LIVE_TV_SORTS.oldest]: (first, second) => first.started_at - second.started_at,
  [SPORT_LIVE_TV_SORTS.alphabeticalAsc]: compareSportLiveTvTitles,
  [SPORT_LIVE_TV_SORTS.alphabeticalDesc]: (first, second) => compareSportLiveTvTitles(second, first),
};

export const sortSportLiveTvEvents = (events, sort) => [...events].sort(SPORT_LIVE_TV_COMPARATORS[sort] ?? SPORT_LIVE_TV_COMPARATORS[SPORT_LIVE_TV_SORTS.newest]);

export const arrangeSportLiveTvEvents = (events, status, sort) => sortSportLiveTvEvents(filterSportLiveTvEventsByStatus(events, status), sort);

export const toggleSportLiveTvValue = (values, value) => (values.includes(value) ? values.filter((current) => current !== value) : [...values, value]);

export const clampSportLiveTvLimit = (limit) => Math.min(limit, SPORT_LIVE_TV_MAX_LIMIT);

export const filterSportLiveTvOptions = (options, search) => {
  const query = search.trim().toLowerCase();

  return query ? options.filter(({ label }) => label.toLowerCase().includes(query)) : options;
};

export const groupSportLiveTvEventsByCategory = (events) =>
  events.reduce((grouped, event) => {
    grouped.set(event.category_id, [...(grouped.get(event.category_id) ?? []), event]);

    return grouped;
  }, new Map());
